/**
 * SmartThings API Integration Service
 * Handles device discovery, control, and management for InnBotics IoT module
 * Routes through backend API for proper authentication and token management
 */

import axios from 'axios';
import { auth } from '../config/firebase';
import { API_BASE } from '../config/apiBase';

// Backend API configuration
const API_BASE_URL = API_BASE;
const SMARTTHINGS_API_BASE = `${API_BASE_URL}/smartthings`;

// Log configuration status (only in development)
if (process.env.NODE_ENV === 'development') {
  console.log('✅ SmartThings service configured to use backend API:', SMARTTHINGS_API_BASE);
}

const smartThingsClient = axios.create({
  baseURL: SMARTTHINGS_API_BASE,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 10000 // 10 second timeout
});

// Add request interceptor to attach Firebase authentication token
smartThingsClient.interceptors.request.use(
  async (config) => {
    try {
      // Wait for auth to be ready
      const currentUser = auth.currentUser;
      if (currentUser) {
        const idToken = await currentUser.getIdToken(false);
        config.headers.Authorization = `Bearer ${idToken}`;
        console.log('🔑 SmartThings API: Token attached for', currentUser.email);
      } else {
        console.warn('⚠️ SmartThings API: No authenticated user, skipping request');
        // Reject the request if no user is authenticated
        throw new Error('User must be authenticated to access SmartThings API');
      }
    } catch (error) {
      console.error('❌ Failed to attach Firebase token:', error);
      throw error;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor for better error handling
smartThingsClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      if (!window.__smartthings_auth_warning_shown) {
        console.warn('⚠️ SmartThings requires authentication. Please log in.');
        window.__smartthings_auth_warning_shown = true;
      }
    }
    return Promise.reject(error);
  }
);

// Error handler wrapper
const handleApiError = (error, context) => {
  const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
  const errorStatus = error.response?.status || 500;
  
  console.error(`SmartThings API Error (${context}):`, {
    message: errorMessage,
    status: errorStatus,
    data: error.response?.data
  });
  
  // Return a structured error instead of throwing
  const apiError = new Error(errorMessage);
  apiError.status = errorStatus;
  apiError.context = context;
  throw apiError;
};

/**
 * Location Management
 */
export const getLocations = async () => {
  try {
    const response = await smartThingsClient.get('/locations');
    return response.data.items || [];
  } catch (error) {
    handleApiError(error, 'getLocations');
  }
};

/**
 * Get manager's hotel SmartThings location only
 * Returns the location associated with the current manager's hotel
 */
export const getMyLocation = async () => {
  try {
    const response = await smartThingsClient.get('/my-location');
    return response.data;
  } catch (error) {
    handleApiError(error, 'getMyLocation');
  }
};

export const createLocation = async (locationData) => {
  try {
    const response = await smartThingsClient.post('/locations', locationData);
    return response.data;
  } catch (error) {
    handleApiError(error, 'createLocation');
  }
};

/**
 * Room Management
 */ 
export const getRooms = async (locationId) => {
  try {
    const response = await smartThingsClient.get(`/locations/${locationId}/rooms`);
    return response.data.items || [];
  } catch (error) {
    handleApiError(error, 'getRooms');
  }
};

export const createRoom = async (locationId, roomData) => {
  try {
    const response = await smartThingsClient.post(`/locations/${locationId}/rooms`, roomData);
    return response.data;
  } catch (error) {
    handleApiError(error, 'createRoom');
  }
};

export const updateRoom = async (locationId, roomId, roomData) => {
  try {
    const response = await smartThingsClient.put(`/locations/${locationId}/rooms/${roomId}`, roomData);
    return response.data;
  } catch (error) {
    handleApiError(error, 'updateRoom');
  }
};

export const deleteRoom = async (locationId, roomId) => {
  try {
    await smartThingsClient.delete(`/locations/${locationId}/rooms/${roomId}`);
    return { success: true };
  } catch (error) {
    handleApiError(error, 'deleteRoom');
  }
};

/**
 * Device Discovery and Management
 */
export const getAllDevices = async (locationId = null) => {
  try {
    const params = locationId ? { locationId } : {};
    const response = await smartThingsClient.get('/devices', { params });
    return response.data.items || [];
  } catch (error) {
    handleApiError(error, 'getAllDevices');
  }
};

export const getDeviceById = async (deviceId) => {
  try {
    const response = await smartThingsClient.get(`/devices/${deviceId}`);
    return response.data;
  } catch (error) {
    handleApiError(error, 'getDeviceById');
  }
};

export const getDeviceStatus = async (deviceId) => {
  try {
    const response = await smartThingsClient.get(`/devices/${deviceId}/status`);
    return response.data.components || {};
  } catch (error) {
    handleApiError(error, 'getDeviceStatus');
  }
};

export const installDevice = async (deviceData) => {
  try {
    const response = await smartThingsClient.post('/devices', deviceData);
    return response.data;
  } catch (error) {
    handleApiError(error, 'installDevice');
  }
};

export const updateDevice = async (deviceId, updates) => {
  try {
    const response = await smartThingsClient.put(`/devices/${deviceId}`, updates);
    return response.data;
  } catch (error) {
    handleApiError(error, 'updateDevice');
  }
};

export const deleteDevice = async (deviceId) => {
  try {
    await smartThingsClient.delete(`/devices/${deviceId}`);
    return { success: true };
  } catch (error) {
    handleApiError(error, 'deleteDevice');
  }
};

/**
 * Device Control Commands
 */
export const executeDeviceCommand = async (deviceId, capability, command, args = []) => {
  try {
    const payload = {
      commands: [
        {
          component: 'main',
          capability,
          command,
          arguments: args
        }
      ]
    };
    const response = await smartThingsClient.post(`/devices/${deviceId}/commands`, payload);
    return response.data;
  } catch (error) {
    handleApiError(error, 'executeDeviceCommand');
  }
};

// Convenience methods for common device controls
export const turnOnDevice = (deviceId) => {
  return executeDeviceCommand(deviceId, 'switch', 'on');
};

export const turnOffDevice = (deviceId) => {
  return executeDeviceCommand(deviceId, 'switch', 'off');
};

export const setLightLevel = (deviceId, level) => {
  return executeDeviceCommand(deviceId, 'switchLevel', 'setLevel', [level]);
};

export const setLightColor = (deviceId, hue, saturation) => {
  return executeDeviceCommand(deviceId, 'colorControl', 'setColor', [{ hue, saturation }]);
};

export const setThermostatMode = (deviceId, mode) => {
  return executeDeviceCommand(deviceId, 'thermostatMode', 'setThermostatMode', [mode]);
};

export const setTemperature = (deviceId, temperature) => {
  return executeDeviceCommand(deviceId, 'thermostatCoolingSetpoint', 'setCoolingSetpoint', [temperature]);
};

export const lockDoor = (deviceId) => {
  return executeDeviceCommand(deviceId, 'lock', 'lock');
};

export const unlockDoor = (deviceId) => {
  return executeDeviceCommand(deviceId, 'lock', 'unlock');
};

/**
 * Device Capabilities
 */
export const getDeviceCapabilities = async (deviceId) => {
  try {
    const device = await getDeviceById(deviceId);
    return device.components?.main?.capabilities || [];
  } catch (error) {
    handleApiError(error, 'getDeviceCapabilities');
  }
};

/**
 * Scenes Management
 */
export const getScenes = async (locationId) => {
  try {
    const response = await smartThingsClient.get('/scenes', {
      params: { locationId }
    });
    return response.data.items || [];
  } catch (error) {
    handleApiError(error, 'getScenes');
  }
};

export const executeScene = async (sceneId) => {
  try {
    const response = await smartThingsClient.post(`/scenes/${sceneId}/execute`);
    return response.data;
  } catch (error) {
    handleApiError(error, 'executeScene');
  }
};

/**
 * Automation Rules (for advanced control)
 */
export const getRules = async (locationId) => {
  try {
    const response = await smartThingsClient.get('/rules', {
      params: { locationId }
    });
    return response.data.items || [];
  } catch (error) {
    handleApiError(error, 'getRules');
  }
};

export const createRule = async (ruleData) => {
  try {
    const response = await smartThingsClient.post('/rules', ruleData);
    return response.data;
  } catch (error) {
    handleApiError(error, 'createRule');
  }
};

export const deleteRule = async (ruleId) => {
  try {
    await smartThingsClient.delete(`/rules/${ruleId}`);
    return { success: true };
  } catch (error) {
    handleApiError(error, 'deleteRule');
  }
};

/**
 * Device Health Check
 */
export const getDeviceHealth = async (deviceId) => {
  try {
    const response = await smartThingsClient.get(`/devices/${deviceId}/health`);
    return response.data;
  } catch (error) {
    handleApiError(error, 'getDeviceHealth');
  }
};

/**
 * Subscription Management (for real-time updates)
 */
export const createSubscription = async (subscriptionData) => {
  try {
    const response = await smartThingsClient.post('/subscriptions', subscriptionData);
    return response.data;
  } catch (error) {
    handleApiError(error, 'createSubscription');
  }
};

export const deleteSubscription = async (subscriptionId) => {
  try {
    await smartThingsClient.delete(`/subscriptions/${subscriptionId}`);
    return { success: true };
  } catch (error) {
    handleApiError(error, 'deleteSubscription');
  }
};

// Helper to categorize devices by type
export const categorizeDevices = (devices) => {
  const categories = {
    lights: [],
    locks: [],
    thermostats: [],
    sensors: [],
    cameras: [],
    switches: [],
    other: []
  };

  devices.forEach(device => {
    const capabilities = device.components?.main?.capabilities || [];
    const capabilityIds = capabilities.map(cap => cap.id);

    if (capabilityIds.includes('switch') && capabilityIds.includes('switchLevel')) {
      categories.lights.push(device);
    } else if (capabilityIds.includes('lock')) {
      categories.locks.push(device);
    } else if (capabilityIds.includes('thermostat')) {
      categories.thermostats.push(device);
    } else if (capabilityIds.includes('motionSensor') || capabilityIds.includes('contactSensor')) {
      categories.sensors.push(device);
    } else if (capabilityIds.includes('videoCamera')) {
      categories.cameras.push(device);
    } else if (capabilityIds.includes('switch')) {
      categories.switches.push(device);
    } else {
      categories.other.push(device);
    }
  });

  return categories;
};

export default {
  // Location
  getLocations,
  getMyLocation,
  createLocation,
  
  // Rooms
  getRooms,
  createRoom,
  updateRoom,
  deleteRoom,
  
  // Devices
  getAllDevices,
  getDeviceById,
  getDeviceStatus,
  installDevice,
  updateDevice,
  deleteDevice,
  
  // Control
  executeDeviceCommand,
  turnOnDevice,
  turnOffDevice,
  setLightLevel,
  setLightColor,
  setThermostatMode,
  setTemperature,
  lockDoor,
  unlockDoor,
  
  // Capabilities
  getDeviceCapabilities,
  
  // Scenes
  getScenes,
  executeScene,
  
  // Rules
  getRules,
  createRule,
  deleteRule,
  
  // Health
  getDeviceHealth,
  
  // Subscriptions
  createSubscription,
  deleteSubscription,
  
  // Helpers
  categorizeDevices
};
